/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  SafeAreaView,
  StatusBar,
  Alert,
  Dimensions,
  TextInput,
  FlatList,
  Modal,
  RefreshControl,
  ActivityIndicator,
  Platform,
  Linking,
  Share,
  TouchableWithoutFeedback
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {
  Ionicons,
  MaterialCommunityIcons,
  FontAwesome5,
  MaterialIcons,
  Feather,
  AntDesign,
} from '@expo/vector-icons';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import { LinearGradient } from 'expo-linear-gradient';
import MapView, { Marker } from 'react-native-maps';

const { width, height } = Dimensions.get('window');

const UKUniversities = () => {
  const navigation = useNavigation();
  const [universities, setUniversities] = useState([]);
  const [filteredUniversities, setFilteredUniversities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [darkMode, setDarkMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [sortBy, setSortBy] = useState('ranking');
  const [compareMode, setCompareMode] = useState(false);
  const [selectedForCompare, setSelectedForCompare] = useState([]);
  const [showMap, setShowMap] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [showSortOptions, setShowSortOptions] = useState(false);
  const [favoriteUniversities, setFavoriteUniversities] = useState([]);
  const [expandedUniversity, setExpandedUniversity] = useState(null);

  // Test IDs
  const testIDs = {
    screen: 'uk-universities-screen',
    searchBar: 'uk-universities-search',
    filterButton: 'filter-button',
    sortButton: 'sort-button',
    mapToggle: 'map-toggle-button',
    compareButton: 'compare-button',
    universityCard: 'university-card-',
    rankingBadge: 'ranking-badge-',
    favoriteButton: 'favorite-button-',
    applyButton: 'apply-button-',
  };

  // Regions for filtering
  const regions = [
    { id: 'all', name: 'All Regions', icon: 'earth' },
    { id: 'england', name: 'England', icon: 'flag' },
    { id: 'scotland', name: 'Scotland', icon: 'flag' },
    { id: 'wales', name: 'Wales', icon: 'flag' },
    { id: 'northern-ireland', name: 'Northern Ireland', icon: 'flag' },
  ];

  // Sort options
  const sortOptions = [
    { id: 'ranking', name: 'Ranking (High to Low)', icon: 'trophy' },
    { id: 'name', name: 'Name (A to Z)', icon: 'sort-alphabetical-ascending' },
    { id: 'tuition', name: 'Tuition (Low to High)', icon: 'cash' },
    { id: 'deadline', name: 'Deadline (Soonest)', icon: 'calendar' },
    { id: 'popularity', name: 'Popularity', icon: 'trending-up' },
  ];

  // Filters
  const filters = [
    { id: 'all', name: 'All Universities', icon: 'university' },
    { id: 'top10', name: 'Top 10 UK', icon: 'crown' },
    { id: 'russell', name: 'Russell Group', icon: 'shield-check' },
    { id: 'international', name: 'International Friendly', icon: 'earth' },
    { id: 'scholarships', name: 'With Scholarships', icon: 'gift' },
    { id: 'placement', name: 'Strong Placement', icon: 'briefcase' },
  ];

  // UK Map coordinates for universities
  const universityCoordinates = {
    'University of Cambridge': { latitude: 52.2053, longitude: 0.1218 },
    'University of Oxford': { latitude: 51.7548, longitude: -1.2544 },
    'Imperial College London': { latitude: 51.4988, longitude: -0.1749 },
    'University of Manchester': { latitude: 53.4668, longitude: -2.2339 },
    'University of Edinburgh': { latitude: 55.9470, longitude: -3.1870 },
    'University of Bristol': { latitude: 51.4560, longitude: -2.6044 },
    'University of Sheffield': { latitude: 53.3811, longitude: -1.4801 },
    'University of Leeds': { latitude: 53.8067, longitude: -1.5550 },
    'University of Glasgow': { latitude: 55.8721, longitude: -4.2882 },
    'University of Southampton': { latitude: 50.9361, longitude: -1.3961 },
    'University of Nottingham': { latitude: 52.9381, longitude: -1.1959 },
    'University of Birmingham': { latitude: 52.4508, longitude: -1.9305 },
    'University of Warwick': { latitude: 52.3792, longitude: -1.5601 },
    'University of Liverpool': { latitude: 53.4064, longitude: -2.9665 },
    'University of Newcastle': { latitude: 54.9783, longitude: -1.6178 },
  };

  const gradients = darkMode ? {
    primary: ['#2d3748', '#4a5568'],
    secondary: ['#805ad5', '#d53f8c'],
    success: ['#38a169', '#68d391'],
    warning: ['#d69e2e', '#ecc94b'],
    danger: ['#e53e3e', '#fc8181'],
    info: ['#3182ce', '#63b3ed'],
    purple: ['#805ad5', '#b794f4'],
    blue: ['#2b6cb0', '#4299e1'],
    green: ['#38a169', '#68d391'],
    teal: ['#319795', '#4fd1c5'],
    orange: ['#dd6b20', '#f6ad55'],
    indigo: ['#4c51bf', '#667eea'],
  } : {
    primary: ['#667eea', '#764ba2'],
    secondary: ['#f093fb', '#f5576c'],
    success: ['#4facfe', '#00f2fe'],
    warning: ['#43e97b', '#38f9d7'],
    danger: ['#fa709a', '#fee140'],
    info: ['#a8edea', '#fed6e3'],
    purple: ['#d299c2', '#fef9d7'],
    blue: ['#2196f3', '#21cbf3'],
    green: ['#56ab2f', '#a8e6cf'],
    teal: ['#667eea', '#764ba2'],
    orange: ['#fdbb2d', '#22c1c3'],
    indigo: ['#667eea', '#764ba2'],
  };

  const styles = getStyles(darkMode);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    filterAndSortUniversities();
  }, [searchQuery, selectedFilter, sortBy, selectedRegion, universities]);

  useFocusEffect(
    React.useCallback(() => {
      loadInitialData();
      return () => {};
    }, [])
  );

  const loadInitialData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Try to load from storage first
      const storedData = await AsyncStorage.getItem('ukUniversitiesData');
      
      if (storedData) {
        const parsedData = JSON.parse(storedData);
        setUniversities(parsedData);
        setFilteredUniversities(parsedData);
      } else {
        // Load mock data if no stored data
        await loadMockData();
      }
      
      // Load favorites
      const favorites = await AsyncStorage.getItem('favoriteUniversities');
      if (favorites) {
        setFavoriteUniversities(JSON.parse(favorites));
      }
      
      // Load dark mode preference
      const darkModePref = await AsyncStorage.getItem('darkMode');
      if (darkModePref !== null) {
        setDarkMode(JSON.parse(darkModePref));
      }
      
    } catch (error) {
      console.error('Error loading initial data:', error);
      setError('Failed to load universities data');
      // Fallback to mock data
      await loadMockData();
    } finally {
      setLoading(false);
    }
  };

  const loadMockData = async () => {
    const mockData = [
      {
        id: 'U001',
        name: 'University of Cambridge',
        location: 'Cambridge, England',
        region: 'england',
        ranking: 1,
        rankingLabel: '#1 in UK (Engineering)',
        worldRanking: 3,
        specializations: [
          'Civil Engineering',
          'Mechanical Engineering',
          'Electrical Engineering',
          'Chemical Engineering',
          'Aerospace Engineering',
          'Biomedical Engineering'
        ],
        website: 'https://www.cam.ac.uk/engineering',
        color: '#002147',
        icon: 'university',
        description: 'World-renowned engineering programs with strong industry links and cutting-edge research facilities. Part of the prestigious Russell Group.',
        tuition: '£9,250/year',
        intlTuition: '£35,000-£45,000/year',
        applicationDeadline: '15 October',
        entryRequirements: 'A*AA including Mathematics and Physics',
        notableAlumni: 'Sir Isaac Newton, Charles Babbage, James Clerk Maxwell',
        acceptanceRate: '21%',
        studentSatisfaction: '92%',
        researchRating: 'World-leading',
        placementRate: '96%',
        avgSalary: '£38,000',
        isRussellGroup: true,
        hasScholarships: true,
        campusSize: 'Large',
        studentPopulation: '21,000',
        internationalStudents: '41%',
        founded: '1209',
        isFavorite: false,
        tags: ['Russell Group', 'Top 10', 'Research Intensive', 'International']
      },
      {
        id: 'U002',
        name: 'University of Oxford',
        location: 'Oxford, England',
        region: 'england',
        ranking: 2,
        rankingLabel: '#2 in UK (Engineering)',
        worldRanking: 4,
        specializations: [
          'Engineering Science',
          'Biomedical Engineering',
          'Materials Science',
          'Aerospace Engineering',
          'Civil Engineering',
          'Electrical Engineering'
        ],
        website: 'https://www.ox.ac.uk/engineering',
        color: '#002147',
        icon: 'university',
        description: 'Interdisciplinary engineering programs with research excellence and strong industry partnerships. Known for tutorial-based teaching.',
        tuition: '£9,250/year',
        intlTuition: '£38,000-£48,000/year',
        applicationDeadline: '15 October',
        entryRequirements: 'A*A*A including Mathematics and Physics',
        notableAlumni: 'Tim Berners-Lee, Stephen Hawking, Robert Hooke',
        acceptanceRate: '17%',
        studentSatisfaction: '94%',
        researchRating: 'World-leading',
        placementRate: '97%',
        avgSalary: '£40,000',
        isRussellGroup: true,
        hasScholarships: true,
        campusSize: 'Large',
        studentPopulation: '24,000',
        internationalStudents: '43%',
        founded: '1096',
        isFavorite: false,
        tags: ['Russell Group', 'Top 10', 'Ancient University', 'Tutorial System']
      },
      {
        id: 'U003',
        name: 'Imperial College London',
        location: 'London, England',
        region: 'england',
        ranking: 3,
        rankingLabel: '#3 in UK (Engineering)',
        worldRanking: 7,
        specializations: [
          'Aeronautical Engineering',
          'Computing',
          'Electrical & Electronic',
          'Chemical Engineering',
          'Mechanical Engineering',
          'Civil Engineering'
        ],
        website: 'https://www.imperial.ac.uk/engineering',
        color: '#00549F',
        icon: 'university',
        description: 'Specialized engineering college with strong industry connections and focus on STEM subjects. Located in the heart of London.',
        tuition: '£9,250/year',
        intlTuition: '£36,000-£42,000/year',
        applicationDeadline: '15 January',
        entryRequirements: 'A*A*A including Mathematics and Physics',
        notableAlumni: 'Alexander Fleming, H. G. Wells, Brian May',
        acceptanceRate: '14%',
        studentSatisfaction: '89%',
        researchRating: 'World-leading',
        placementRate: '95%',
        avgSalary: '£42,000',
        isRussellGroup: true,
        hasScholarships: true,
        campusSize: 'Urban',
        studentPopulation: '19,000',
        internationalStudents: '59%',
        founded: '1907',
        isFavorite: false,
        tags: ['Russell Group', 'Top 10', 'STEM Focus', 'London']
      },
      {
        id: 'U004',
        name: 'University of Manchester',
        location: 'Manchester, England',
        region: 'england',
        ranking: 4,
        rankingLabel: '#4 in UK (Engineering)',
        worldRanking: 27,
        specializations: [
          'Chemical Engineering',
          'Mechanical Engineering',
          'Civil Engineering',
          'Computer Science',
          'Electrical Engineering',
          'Materials Science'
        ],
        website: 'https://www.manchester.ac.uk/engineering',
        color: '#E31B23',
        icon: 'university',
        description: 'Largest engineering faculty in the UK with extensive facilities and strong industry links. Known for innovation and research.',
        tuition: '£9,250/year',
        intlTuition: '£28,000-£35,000/year',
        applicationDeadline: '25 January',
        entryRequirements: 'AAA including Mathematics and Science',
        notableAlumni: 'Alan Turing, Ernest Rutherford, John Dalton',
        acceptanceRate: '56%',
        studentSatisfaction: '87%',
        researchRating: 'World-leading',
        placementRate: '93%',
        avgSalary: '£34,000',
        isRussellGroup: true,
        hasScholarships: true,
        campusSize: 'Very Large',
        studentPopulation: '40,000',
        internationalStudents: '38%',
        founded: '1824',
        isFavorite: false,
        tags: ['Russell Group', 'Top 10', 'Large Campus', 'Industrial Links']
      },
      {
        id: 'U005',
        name: 'University of Edinburgh',
        location: 'Edinburgh, Scotland',
        region: 'scotland',
        ranking: 5,
        rankingLabel: '#5 in UK (Engineering)',
        worldRanking: 16,
        specializations: [
          'Electronics & Computer Science',
          'Civil & Environmental',
          'Mechanical Engineering',
          'Chemical Engineering',
          'Electrical Engineering',
          'Renewable Energy'
        ],
        website: 'https://www.ed.ac.uk/engineering',
        color: '#003366',
        icon: 'university',
        description: 'Leading Scottish university with strong research output and beautiful historic campus. Free tuition for Scottish/EU students.',
        tuition: '£1,820/year (Scotland/EU)',
        intlTuition: '£28,950-£34,800/year',
        applicationDeadline: '30 June',
        entryRequirements: 'AAA-ABB including Mathematics',
        notableAlumni: 'James Clerk Maxwell, Alexander Graham Bell, Charles Darwin',
        acceptanceRate: '48%',
        studentSatisfaction: '85%',
        researchRating: 'World-leading',
        placementRate: '91%',
        avgSalary: '£32,000',
        isRussellGroup: true,
        hasScholarships: true,
        campusSize: 'Large',
        studentPopulation: '35,000',
        internationalStudents: '42%',
        founded: '1583',
        isFavorite: false,
        tags: ['Russell Group', 'Top 10', 'Scottish', 'Historic']
      },
      {
        id: 'U006',
        name: 'University of Bristol',
        location: 'Bristol, England',
        region: 'england',
        ranking: 6,
        rankingLabel: '#6 in UK (Engineering)',
        worldRanking: 58,
        specializations: [
          'Aerospace Engineering',
          'Civil Engineering',
          'Mechanical Engineering',
          'Electrical Engineering',
          'Computer Science',
          'Robotics'
        ],
        website: 'https://www.bristol.ac.uk/engineering',
        color: '#E03C31',
        icon: 'university',
        description: 'Innovative engineering programs with strong industry placement and research opportunities. Beautiful campus in vibrant city.',
        tuition: '£9,250/year',
        intlTuition: '£26,000-£34,000/year',
        applicationDeadline: '29 January',
        entryRequirements: 'A*AA including Mathematics',
        notableAlumni: 'Paul Dirac, David Olusoga, Will Hutton',
        acceptanceRate: '67%',
        studentSatisfaction: '88%',
        researchRating: 'World-leading',
        placementRate: '94%',
        avgSalary: '£33,000',
        isRussellGroup: true,
        hasScholarships: true,
        campusSize: 'Large',
        studentPopulation: '23,000',
        internationalStudents: '28%',
        founded: '1876',
        isFavorite: false,
        tags: ['Russell Group', 'Top 10', 'Industry Placement', 'Research']
      },
      {
        id: 'U007',
        name: 'University of Sheffield',
        location: 'Sheffield, England',
        region: 'england',
        ranking: 7,
        rankingLabel: '#7 in UK (Engineering)',
        worldRanking: 95,
        specializations: [
          'Materials Science & Engineering',
          'Civil & Structural',
          'Mechanical Engineering',
          'Electronic Engineering',
          'Aerospace Engineering',
          'Biomedical Engineering'
        ],
        website: 'https://www.sheffield.ac.uk/engineering',
        color: '#0C2340',
        icon: 'university',
        description: 'Renowned for materials science and engineering research with excellent industry connections and student satisfaction.',
        tuition: '£9,250/year',
        intlTuition: '£25,000-£33,000/year',
        applicationDeadline: '29 January',
        entryRequirements: 'AAA including Mathematics and Science',
        notableAlumni: 'Harold Kroto, Helen Sharman, David Blunkett',
        acceptanceRate: '74%',
        studentSatisfaction: '86%',
        researchRating: 'World-leading',
        placementRate: '92%',
        avgSalary: '£31,000',
        isRussellGroup: true,
        hasScholarships: true,
        campusSize: 'Large',
        studentPopulation: '29,000',
        internationalStudents: '31%',
        founded: '1905',
        isFavorite: false,
        tags: ['Russell Group', 'Top 10', 'Materials Science', 'Student Satisfaction']
      },
      {
        id: 'U008',
        name: 'University of Leeds',
        location: 'Leeds, England',
        region: 'england',
        ranking: 8,
        rankingLabel: '#8 in UK (Engineering)',
        worldRanking: 92,
        specializations: [
          'Civil Engineering',
          'Mechanical Engineering',
          'Electronic Engineering',
          'Chemical Engineering',
          'Aeronautical Engineering',
          'Robotics'
        ],
        website: 'https://eps.leeds.ac.uk/engineering',
        color: '#014421',
        icon: 'university',
        description: 'Comprehensive engineering programs with excellent facilities and strong graduate employment rates. Large single-site campus.',
        tuition: '£9,250/year',
        intlTuition: '£25,000-£32,000/year',
        applicationDeadline: '29 January',
        entryRequirements: 'AAA including Mathematics',
        notableAlumni: 'Jack Straw, Mark Knopfler, Chris Pine',
        acceptanceRate: '77%',
        studentSatisfaction: '84%',
        researchRating: 'World-leading',
        placementRate: '90%',
        avgSalary: '£30,000',
        isRussellGroup: true,
        hasScholarships: true,
        campusSize: 'Very Large',
        studentPopulation: '38,000',
        internationalStudents: '33%',
        founded: '1904',
        isFavorite: false,
        tags: ['Russell Group', 'Top 10', 'Comprehensive', 'Employment Rate']
      },
      {
        id: 'U009',
        name: 'University of Glasgow',
        location: 'Glasgow, Scotland',
        region: 'scotland',
        ranking: 9,
        rankingLabel: '#9 in UK (Engineering)',
        worldRanking: 73,
        specializations: [
          'Civil Engineering',
          'Mechanical Engineering',
          'Aerospace Engineering',
          'Biomedical Engineering',
          'Electrical Engineering',
          'Renewable Energy'
        ],
        website: 'https://www.gla.ac.uk/engineering',
        color: '#003366',
        icon: 'university',
        description: 'Historic university with strong engineering programs and excellent research facilities. Part of the prestigious Russell Group.',
        tuition: '£1,820/year (Scotland/EU)',
        intlTuition: '£23,000-£29,000/year',
        applicationDeadline: '30 June',
        entryRequirements: 'AAB-BBB including Mathematics',
        notableAlumni: 'Adam Smith, James Watt, Lord Kelvin',
        acceptanceRate: '70%',
        studentSatisfaction: '83%',
        researchRating: 'World-leading',
        placementRate: '89%',
        avgSalary: '£29,000',
        isRussellGroup: true,
        hasScholarships: true,
        campusSize: 'Large',
        studentPopulation: '29,000',
        internationalStudents: '35%',
        founded: '1451',
        isFavorite: false,
        tags: ['Russell Group', 'Top 10', 'Scottish', 'Historic']
      },
      {
        id: 'U010',
        name: 'University of Southampton',
        location: 'Southampton, England',
        region: 'england',
        ranking: 10,
        rankingLabel: '#10 in UK (Engineering)',
        worldRanking: 77,
        specializations: [
          'Maritime Engineering',
          'Aeronautical Engineering',
          'Mechanical Engineering',
          'Electrical Engineering',
          'Computer Science',
          'Civil Engineering'
        ],
        website: 'https://www.southampton.ac.uk/engineering',
        color: '#E03A3E',
        icon: 'university',
        description: 'Strong engineering programs with particular excellence in maritime and aeronautical engineering. Excellent industry connections.',
        tuition: '£9,250/year',
        intlTuition: '£22,000-£28,000/year',
        applicationDeadline: '29 January',
        entryRequirements: 'AAA-ABB including Mathematics',
        notableAlumni: 'Tim Berners-Lee, Christopher Nolan, Edwina Currie',
        acceptanceRate: '84%',
        studentSatisfaction: '82%',
        researchRating: 'World-leading',
        placementRate: '88%',
        avgSalary: '£28,000',
        isRussellGroup: true,
        hasScholarships: true,
        campusSize: 'Large',
        studentPopulation: '25,000',
        internationalStudents: '30%',
        founded: '1862',
        isFavorite: false,
        tags: ['Russell Group', 'Top 10', 'Maritime', 'Aeronautical']
      },
    ];

    setUniversities(mockData);
    setFilteredUniversities(mockData);
    
    // Save to storage for future use
    await AsyncStorage.setItem('ukUniversitiesData', JSON.stringify(mockData));
  };

  const filterAndSortUniversities = () => {
    let filtered = [...universities];

    // Apply search filter
    if (searchQuery.trim()) {
      filtered = filtered.filter(university =>
        String(university.name ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        String(university.location ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        university.specializations.some(spec =>
          spec.toLowerCase().includes(searchQuery.toLowerCase())
        )
      );
    }

    // Apply region filter
    if (selectedRegion !== 'all') {
      filtered = filtered.filter(university => 
        university.region === selectedRegion
      );
    }

    // Apply category filter
    if (selectedFilter !== 'all') {
      switch (selectedFilter) {
        case 'top10':
          filtered = filtered.filter(u => u.ranking <= 10);
          break;
        case 'russell':
          filtered = filtered.filter(u => u.isRussellGroup);
          break;
        case 'international':
          filtered = filtered.filter(u => u.internationalStudents > 30);
          break;
        case 'scholarships':
          filtered = filtered.filter(u => u.hasScholarships);
          break;
        case 'placement':
          filtered = filtered.filter(u => u.placementRate >= 90);
          break;
      }
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'ranking':
          return a.ranking - b.ranking;
        case 'name':
          return a.name.localeCompare(b.name);
        case 'tuition':
          const tuitionA = parseInt(a.intlTuition.replace(/[^0-9]/g, '').split('-')[0]);
          const tuitionB = parseInt(b.intlTuition.replace(/[^0-9]/g, '').split('-')[0]);
          return tuitionA - tuitionB;
        case 'deadline':
          // Simplified sorting by deadline month
          const monthOrder = { 'January': 1, 'October': 10, 'June': 6 };
          const monthA = a.applicationDeadline.split(' ')[1] || a.applicationDeadline;
          const monthB = b.applicationDeadline.split(' ')[1] || b.applicationDeadline;
          return monthOrder[monthA] - monthOrder[monthB];
        case 'popularity':
          return b.studentPopulation - a.studentPopulation;
        default:
          return a.ranking - b.ranking;
      }
    });

    setFilteredUniversities(filtered);
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await loadInitialData();
    setIsRefreshing(false);
  };

  const toggleFavorite = async (universityId) => {
    try {
      let newFavorites;
      if (favoriteUniversities.includes(universityId)) {
        newFavorites = favoriteUniversities.filter(id => id !== universityId);
      } else {
        newFavorites = [...favoriteUniversities, universityId];
      }
      
      setFavoriteUniversities(newFavorites);
      await AsyncStorage.setItem('favoriteUniversities', JSON.stringify(newFavorites));
      
      // Update university isFavorite status
      setUniversities(prev =>
        prev.map(u => ({
          ...u,
          isFavorite: newFavorites.includes(u.id)
        }))
      );
      
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  const toggleCompare = (universityId) => {
    if (selectedForCompare.includes(universityId)) {
      setSelectedForCompare(prev => prev.filter(id => id !== universityId));
    } else {
      if (selectedForCompare.length >= 3) {
        Alert.alert('Maximum Selection', 'You can compare up to 3 universities at a time.');
        return;
      }
      setSelectedForCompare(prev => [...prev, universityId]);
    }
  };

  const handleCompare = () => {
    if (selectedForCompare.length < 2) {
      Alert.alert('Select Universities', 'Please select at least 2 universities to compare.');
      return;
    }
    
    const selectedUnis = universities.filter(u => selectedForCompare.includes(u.id));
    navigation.navigate('UniversityCompare', { universities: selectedUnis });
    setSelectedForCompare([]);
  };

  const openWebsite = async (url) => {
    try {
      await Linking.openURL(url);
    } catch (error) {
      console.error('Error opening URL:', error);
      Alert.alert('Error', 'Failed to open website.');
    }
  };

  const shareUniversity = async (university) => {
    try {
      await Share.share({
        title: `Check out ${university.name}`,
        message: `${university.name} - ${university.rankingLabel}\n\nLocation: ${university.location}\nTuition: ${university.intlTuition}\nDeadline: ${university.applicationDeadline}\n\n${university.description}`,
      });
    } catch (error) {
      console.error('Error sharing university:', error);
    }
  };

  const getRegionStats = () => {
    const stats = {
      total: universities.length,
      england: universities.filter(u => u.region === 'england').length,
      scotland: universities.filter(u => u.region === 'scotland').length,
      wales: universities.filter(u => u.region === 'wales').length,
      'northern-ireland': universities.filter(u => u.region === 'northern-ireland').length,
      russellGroup: universities.filter(u => u.isRussellGroup).length,
      top10: universities.filter(u => u.ranking <= 10).length,
      withScholarships: universities.filter(u => u.hasScholarships).length,
    };
    return stats;
  };

  const renderHeader = () => (
    <LinearGradient
      colors={gradients.primary}
      start={{ x: 0, y: 0 }}
      end={{ x: 1, y: 0 }}
      style={styles.header}
    >
      <TouchableOpacity 
        style={styles.backButton}
        onPress={() => navigation.goBack()}
      >
        <MaterialCommunityIcons name="arrow-left" size={24} color="#ffffff" />
      </TouchableOpacity>
      <View style={styles.headerTitleContainer}>
        <Text style={styles.headerTitle}>🎓 UK Engineering Universities</Text>
        <Text style={styles.headerSubtitle}>
          Top {universities.length} institutions for engineering education
        </Text>
      </View>
      <TouchableOpacity 
        style={styles.compareButton}
        onPress={() => setCompareMode(!compareMode)}
        testID={testIDs.compareButton}
      >
        <MaterialCommunityIcons 
          name="compare" 
          size={24} 
          color={compareMode ? "#fbbf24" : "#ffffff"} 
        />
        {selectedForCompare.length > 0 && (
          <View style={styles.compareBadge}>
            <Text style={styles.compareBadgeText}>{selectedForCompare.length}</Text>
          </View>
        )}
      </TouchableOpacity>
    </LinearGradient>
  );

  const renderSearchBar = () => (
    <View style={styles.searchContainer}>
      <View style={styles.searchInputContainer}>
        <MaterialCommunityIcons 
          name="magnify" 
          size={24} 
          color={darkMode ? "#a0aec0" : "#6b7280"} 
          style={styles.searchIcon}
        />
        <TextInput
          style={styles.searchInput}
          placeholder="Search universities, locations, specializations..."
          value={searchQuery}
          onChangeText={setSearchQuery}
          placeholderTextColor={darkMode ? "#a0aec0" : "#6b7280"}
          testID={testIDs.searchBar}
        />
        {searchQuery.length > 0 && (
          <TouchableOpacity onPress={() => setSearchQuery('')}>
            <MaterialCommunityIcons 
              name="close-circle" 
              size={20} 
              color={darkMode ? "#a0aec0" : "#6b7280"} 
            />
          </TouchableOpacity>
        )}
      </View>
    </View>
  );

  const renderStats = () => {
    const stats = getRegionStats();
    
    return (
      <View style={styles.statsContainer}>
        <View style={styles.statsGrid}>
          <View style={styles.statCard}>
            <Text style={styles.statNumber}>{stats.total}</Text>
            <Text style={styles.statLabel}>Total</Text>
            <Text style={styles.statSubLabel}>Universities</Text>
          </View>
          <View style={styles.statCard}>
            <Text style={styles.statNumber}>{stats.russellGroup}</Text>
            <Text style={styles.statLabel}>Russell</Text>
            <Text style={styles.statSubLabel}>Group</Text>
          </View>
          <View style={styles.statCard}>
            <Text style={styles.statNumber}>{stats.top10}</Text>
            <Text style={styles.statLabel}>Top 10</Text>
            <Text style={styles.statSubLabel}>UK Ranking</Text>
          </View>
          <View style={styles.statCard}>
            <Text style={styles.statNumber}>{stats.withScholarships}</Text>
            <Text style={styles.statLabel}>With</Text>
            <Text style={styles.statSubLabel}>Scholarships</Text>
          </View>
        </View>
      </View>
    );
  };

  const renderQuickFilters = () => (
    <View style={styles.quickFiltersContainer}>
      <ScrollView horizontal showsHorizontalScrollIndicator={false}>
        <TouchableOpacity
          style={[styles.quickFilter, selectedRegion === 'all' && styles.quickFilterActive]}
          onPress={() => setSelectedRegion('all')}
        >
          <MaterialCommunityIcons 
            name="earth" 
            size={16} 
            color={selectedRegion === 'all' ? '#ffffff' : (darkMode ? '#a0aec0' : '#6b7280')} 
          />
          <Text style={[styles.quickFilterText, selectedRegion === 'all' && styles.quickFilterTextActive]}>
            All UK
          </Text>
        </TouchableOpacity>
        
        <TouchableOpacity
          style={[styles.quickFilter, selectedRegion === 'england' && styles.quickFilterActive]}
          onPress={() => setSelectedRegion('england')}
        >
          <MaterialCommunityIcons 
            name="flag" 
            size={16} 
            color={selectedRegion === 'england' ? '#ffffff' : (darkMode ? '#a0aec0' : '#6b7280')} 
          />
          <Text style={[styles.quickFilterText, selectedRegion === 'england' && styles.quickFilterTextActive]}>
            England ({getRegionStats().england})
          </Text>
        </TouchableOpacity>
        
        <TouchableOpacity
          style={[styles.quickFilter, selectedRegion === 'scotland' && styles.quickFilterActive]}
          onPress={() => setSelectedRegion('scotland')}
        >
          <MaterialCommunityIcons 
            name="flag" 
            size={16} 
            color={selectedRegion === 'scotland' ? '#ffffff' : (darkMode ? '#a0aec0' : '#6b7280')} 
          />
          <Text style={[styles.quickFilterText, selectedRegion === 'scotland' && styles.quickFilterTextActive]}>
            Scotland ({getRegionStats().scotland})
          </Text>
        </TouchableOpacity>
      </ScrollView>
    </View>
  );

  const renderActionButtons = () => (
    <View style={styles.actionButtonsContainer}>
      <TouchableOpacity
        style={styles.actionButton}
        onPress={() => setShowFilters(!showFilters)}
        testID={testIDs.filterButton}
      >
        <MaterialCommunityIcons 
          name="filter-variant" 
          size={20} 
          color={darkMode ? "#cbd5e0" : "#4b5563"} 
        />
        <Text style={styles.actionButtonText}>Filter</Text>
        {selectedFilter !== 'all' && (
          <View style={styles.actionBadge} />
        )}
      </TouchableOpacity>
      
      <TouchableOpacity
        style={styles.actionButton}
        onPress={() => setShowSortOptions(!showSortOptions)}
        testID={testIDs.sortButton}
      >
        <MaterialCommunityIcons 
          name="sort" 
          size={20} 
          color={darkMode ? "#cbd5e0" : "#4b5563"} 
        />
        <Text style={styles.actionButtonText}>Sort</Text>
      </TouchableOpacity>
      
      <TouchableOpacity
        style={styles.actionButton}
        onPress={() => setShowMap(!showMap)}
        testID={testIDs.mapToggle}
      >
        <MaterialCommunityIcons 
          name={showMap ? "format-list-bulleted" : "map"} 
          size={20} 
          color={darkMode ? "#cbd5e0" : "#4b5563"} 
        />
        <Text style={styles.actionButtonText}>{showMap ? 'List' : 'Map'}</Text>
      </TouchableOpacity>
      
      {compareMode && selectedForCompare.length > 0 && (
        <TouchableOpacity
          style={[styles.actionButton, styles.compareActionButton]}
          onPress={handleCompare}
        >
          <MaterialCommunityIcons name="compare" size={20} color="#ffffff" />
          <Text style={[styles.actionButtonText, { color: '#ffffff' }]}>
            Compare ({selectedForCompare.length})
          </Text>
        </TouchableOpacity>
      )}
    </View>
  );

  const renderFiltersModal = () => (
    <Modal
      visible={showFilters}
      transparent
      animationType="slide"
      onRequestClose={() => setShowFilters(false)}
    >
      <TouchableWithoutFeedback onPress={() => setShowFilters(false)}>
        <View style={styles.modalOverlay}>
          <TouchableWithoutFeedback>
            <View style={styles.filtersModal}>
              <View style={styles.modalHeader}>
                <Text style={styles.modalTitle}>Filter Universities</Text>
                <TouchableOpacity onPress={() => setShowFilters(false)}>
                  <MaterialCommunityIcons name="close" size={24} color={darkMode ? "#f7fafc" : "#1f2937"} />
                </TouchableOpacity>
              </View>
              
              <ScrollView style={styles.filtersList}>
                {filters.map(filter => (
                  <TouchableOpacity
                    key={filter.id}
                    style={[
                      styles.filterItem,
                      selectedFilter === filter.id && styles.filterItemActive
                    ]}
                    onPress={() => {
                      setSelectedFilter(filter.id);
                      setShowFilters(false);
                    }}
                  >
                    <View style={styles.filterIconContainer}>
                      <MaterialCommunityIcons 
                        name={filter.icon} 
                        size={20} 
                        color={selectedFilter === filter.id ? '#667eea' : (darkMode ? '#a0aec0' : '#6b7280')} 
                      />
                    </View>
                    <Text style={[
                      styles.filterText,
                      selectedFilter === filter.id && styles.filterTextActive
                    ]}>
                      {filter.name}
                    </Text>
                    {selectedFilter === filter.id && (
                      <MaterialCommunityIcons name="check" size={20} color="#667eea" />
                    )}
                  </TouchableOpacity>
                ))}
              </ScrollView>
              
              <TouchableOpacity
                style={styles.clearFiltersButton}
                onPress={() => {
                  setSelectedFilter('all');
                  setShowFilters(false);
                }}
              >
                <Text style={styles.clearFiltersText}>Clear All Filters</Text>
              </TouchableOpacity>
            </View>
          </TouchableWithoutFeedback>
        </View>
      </TouchableWithoutFeedback>
    </Modal>
  );

  const renderSortModal = () => (
    <Modal
      visible={showSortOptions}
      transparent
      animationType="slide"
      onRequestClose={() => setShowSortOptions(false)}
    >
      <TouchableWithoutFeedback onPress={() => setShowSortOptions(false)}>
        <View style={styles.modalOverlay}>
          <TouchableWithoutFeedback>
            <View style={styles.sortModal}>
              <View style={styles.modalHeader}>
                <Text style={styles.modalTitle}>Sort By</Text>
                <TouchableOpacity onPress={() => setShowSortOptions(false)}>
                  <MaterialCommunityIcons name="close" size={24} color={darkMode ? "#f7fafc" : "#1f2937"} />
                </TouchableOpacity>
              </View>
              
              <View style={styles.sortOptionsList}>
                {sortOptions.map(option => (
                  <TouchableOpacity
                    key={option.id}
                    style={[
                      styles.sortItem,
                      sortBy === option.id && styles.sortItemActive
                    ]}
                    onPress={() => {
                      setSortBy(option.id);
                      setShowSortOptions(false);
                    }}
                  >
                    <View style={styles.sortIconContainer}>
                      <MaterialCommunityIcons 
                        name={option.icon} 
                        size={20} 
                        color={sortBy === option.id ? '#667eea' : (darkMode ? '#a0aec0' : '#6b7280')} 
                      />
                    </View>
                    <Text style={[
                      styles.sortText,
                      sortBy === option.id && styles.sortTextActive
                    ]}>
                      {option.name}
                    </Text>
                    {sortBy === option.id && (
                      <MaterialCommunityIcons name="check" size={20} color="#667eea" />
                    )}
                  </TouchableOpacity>
                ))}
              </View>
            </View>
          </TouchableWithoutFeedback>
        </View>
      </TouchableWithoutFeedback>
    </Modal>
  );

  const renderUniversityCard = (university, index) => {
    const isFavorite = favoriteUniversities.includes(university.id);
    const isSelected = selectedForCompare.includes(university.id);
    
    return (
      <TouchableOpacity
        key={university.id}
        style={[
          styles.universityCard,
          compareMode && isSelected && styles.universityCardSelected,
          expandedUniversity === university.id && styles.universityCardExpanded
        ]}
        onPress={() => {
          if (compareMode) {
            toggleCompare(university.id);
          } else {
            navigation.navigate('UniversityDetails', { university });
          }
        }}
        onLongPress={() => setExpandedUniversity(
          expandedUniversity === university.id ? null : university.id
        )}
        testID={`${testIDs.universityCard}${index}`}
      >
        <LinearGradient
          colors={[university.color, `${university.color}80`]}
          style={styles.universityCardHeader}
        >
          <View style={styles.universityHeaderLeft}>
            <View style={styles.rankingBadge} testID={`${testIDs.rankingBadge}${index}`}>
              <Text style={styles.rankingText}>#{university.ranking}</Text>
            </View>
            <View style={styles.universityBasicInfo}>
              <Text style={styles.universityName} numberOfLines={2}>
                {university.name}
              </Text>
              <Text style={styles.universityLocation}>
                <MaterialCommunityIcons name="map-marker" size={12} color="rgba(255,255,255,0.9)" />
                {' '}{university.location}
              </Text>
            </View>
          </View>
          
          <View style={styles.universityHeaderRight}>
            <TouchableOpacity
              style={styles.favoriteButton}
              onPress={(e) => {
                e.stopPropagation();
                toggleFavorite(university.id);
              }}
              testID={`${testIDs.favoriteButton}${index}`}
            >
              <MaterialCommunityIcons 
                name={isFavorite ? "heart" : "heart-outline"} 
                size={24} 
                color={isFavorite ? "#ef4444" : "#ffffff"} 
              />
            </TouchableOpacity>
            
            {compareMode && (
              <View style={styles.compareCheckbox}>
                <MaterialCommunityIcons 
                  name={isSelected ? "checkbox-marked" : "checkbox-blank-outline"} 
                  size={24} 
                  color={isSelected ? "#fbbf24" : "#ffffff"} 
                />
              </View>
            )}
          </View>
        </LinearGradient>
        
        <View style={styles.universityCardContent}>
          <Text style={styles.universityDescription} numberOfLines={expandedUniversity === university.id ? 10 : 3}>
            {university.description}
          </Text>
          
          <View style={styles.universityStats}>
            <View style={styles.statItemSmall}>
              <MaterialCommunityIcons name="school" size={14} color={darkMode ? "#a0aec0" : "#6b7280"} />
              <Text style={styles.statTextSmall}>{university.studentPopulation.toLocaleString()} students</Text>
            </View>
            <View style={styles.statItemSmall}>
              <MaterialCommunityIcons name="cash" size={14} color={darkMode ? "#a0aec0" : "#6b7280"} />
              <Text style={styles.statTextSmall}>{university.intlTuition}</Text>
            </View>
            <View style={styles.statItemSmall}>
              <MaterialCommunityIcons name="calendar" size={14} color={darkMode ? "#a0aec0" : "#6b7280"} />
              <Text style={styles.statTextSmall}>{university.applicationDeadline}</Text>
            </View>
          </View>
          
          <View style={styles.specializationsContainer}>
            <Text style={styles.specializationsTitle}>Specializations:</Text>
            <View style={styles.specializationsList}>
              {university.specializations.slice(0, 3).map((spec, idx) => (
                <View key={idx} style={styles.specializationTag}>
                  <Text style={styles.specializationText}>{spec}</Text>
                </View>
              ))}
              {university.specializations.length > 3 && (
                <View style={styles.moreTag}>
                  <Text style={styles.moreText}>+{university.specializations.length - 3}</Text>
                </View>
              )}
            </View>
          </View>
          
          {expandedUniversity === university.id && (
            <View style={styles.expandedDetails}>
              <View style={styles.detailRow}>
                <MaterialCommunityIcons name="star" size={14} color="#fbbf24" />
                <Text style={styles.detailLabel}>World Ranking: </Text>
                <Text style={styles.detailValue}>#{university.worldRanking}</Text>
              </View>
              <View style={styles.detailRow}>
                <MaterialCommunityIcons name="account-group" size={14} color={darkMode ? "#a0aec0" : "#6b7280"} />
                <Text style={styles.detailLabel}>Acceptance Rate: </Text>
                <Text style={styles.detailValue}>{university.acceptanceRate}</Text>
              </View>
              <View style={styles.detailRow}>
                <MaterialCommunityIcons name="briefcase" size={14} color={darkMode ? "#a0aec0" : "#6b7280"} />
                <Text style={styles.detailLabel}>Placement Rate: </Text>
                <Text style={styles.detailValue}>{university.placementRate}</Text>
              </View>
              <View style={styles.detailRow}>
                <MaterialCommunityIcons name="cash" size={14} color={darkMode ? "#a0aec0" : "#6b7280"} />
                <Text style={styles.detailLabel}>Avg Salary: </Text>
                <Text style={styles.detailValue}>{university.avgSalary}</Text>
              </View>
            </View>
          )}
          
          <View style={styles.universityActions}>
            <TouchableOpacity
              style={[styles.actionButtonSmall, styles.websiteButton]}
              onPress={(e) => {
                e.stopPropagation();
                openWebsite(university.website);
              }}
            >
              <MaterialCommunityIcons name="web" size={16} color="#ffffff" />
              <Text style={styles.actionButtonSmallText}>Website</Text>
            </TouchableOpacity>
            
            <TouchableOpacity
              style={[styles.actionButtonSmall, styles.applyButton]}
              onPress={(e) => {
                e.stopPropagation();
                navigation.navigate('UniversityApply', { university });
              }}
              testID={`${testIDs.applyButton}${index}`}
            >
              <MaterialCommunityIcons name="send" size={16} color="#ffffff" />
              <Text style={styles.actionButtonSmallText}>Apply</Text>
            </TouchableOpacity>
            
            <TouchableOpacity
              style={[styles.actionButtonSmall, styles.shareButton]}
              onPress={(e) => {
                e.stopPropagation();
                shareUniversity(university);
              }}
            >
              <MaterialCommunityIcons name="share-variant" size={16} color="#ffffff" />
              <Text style={styles.actionButtonSmallText}>Share</Text>
            </TouchableOpacity>
          </View>
        </View>
      </TouchableOpacity>
    );
  };

  const renderMapView = () => (
    <View style={styles.mapContainer}>
      <MapView
        style={styles.map}
        initialRegion={{
          latitude: 54.0,
          longitude: -2.0,
          latitudeDelta: 10.0,
          longitudeDelta: 10.0,
        }}
      >
        {universities.map((university, index) => {
          const coordinates = universityCoordinates[university.name];
          if (coordinates) {
            return (
              <Marker
                key={university.id}
                coordinate={coordinates}
                title={university.name}
                description={university.location}
                onPress={() => navigation.navigate('UniversityDetails', { university })}
              >
                <View style={styles.mapMarker}>
                  <LinearGradient
                    colors={[university.color, `${university.color}80`]}
                    style={styles.markerGradient}
                  >
                    <Text style={styles.markerText}>#{university.ranking}</Text>
                  </LinearGradient>
                </View>
              </Marker>
            );
          }
          return null;
        })}
      </MapView>
      
      <TouchableOpacity
        style={styles.mapLegend}
        onPress={() => setShowMap(false)}
      >
        <Text style={styles.mapLegendText}>
          📍 {universities.length} universities on map • Tap to return to list
        </Text>
      </TouchableOpacity>
    </View>
  );

  const renderEmptyState = () => (
    <View style={styles.emptyState}>
      <MaterialCommunityIcons name="university" size={64} color={darkMode ? "#4a5568" : "#d1d5db"} />
      <Text style={styles.emptyStateTitle}>No universities found</Text>
      <Text style={styles.emptyStateText}>
        {searchQuery 
          ? 'Try adjusting your search or filters'
          : 'Unable to load universities data'}
      </Text>
      <TouchableOpacity style={styles.emptyStateButton} onPress={handleRefresh}>
        <Text style={styles.emptyStateButtonText}>Refresh</Text>
      </TouchableOpacity>
    </View>
  );

  const renderLoading = () => (
    <View style={styles.loadingContainer}>
      <ActivityIndicator size={36} color="#667eea" />
      <Text style={styles.loadingText}>Loading universities...</Text>
    </View>
  );

  const renderError = () => (
    <View style={styles.errorContainer}>
      <MaterialCommunityIcons name="alert-circle" size={64} color="#ef4444" />
      <Text style={styles.errorTitle}>Error loading data</Text>
      <Text style={styles.errorText}>{error}</Text>
      <TouchableOpacity style={styles.retryButton} onPress={loadInitialData}>
        <Text style={styles.retryButtonText}>Retry</Text>
      </TouchableOpacity>
    </View>
  );

  if (loading) {
    return renderLoading();
  }

  if (error) {
    return renderError();
  }

  return (
    <SafeAreaView style={styles.safeArea} testID={testIDs.screen}>
      <StatusBar barStyle="light-content" backgroundColor="#667eea" />
      
      {renderHeader()}
      
      {renderSearchBar()}
      
      {!showMap && renderStats()}
      
      {!showMap && renderQuickFilters()}
      
      {renderActionButtons()}
      
      {showMap ? (
        renderMapView()
      ) : (
        <FlatList
          data={filteredUniversities}
          renderItem={({ item, index }) => renderUniversityCard(item, index)}
          keyExtractor={(item) => item.id}
          contentContainerStyle={styles.listContent}
          showsVerticalScrollIndicator={false}
          refreshControl={
            <RefreshControl
              refreshing={isRefreshing}
              onRefresh={handleRefresh}
              colors={['#667eea']}
              tintColor="#667eea"
            />
          }
          ListEmptyComponent={renderEmptyState}
          ListFooterComponent={() => (
            <View style={styles.footer}>
              <Text style={styles.footerText}>
                Showing {filteredUniversities.length} of {universities.length} universities
              </Text>
              <Text style={styles.footerNote}>
                Data is for reference only. Please verify with official university websites.
              </Text>
            </View>
          )}
        />
      )}
      
      {renderFiltersModal()}
      {renderSortModal()}
      
      {compareMode && (
        <View style={styles.compareModeBanner}>
          <Text style={styles.compareModeText}>
            👉 Select universities to compare (max 3)
          </Text>
          <TouchableOpacity onPress={() => setCompareMode(false)}>
            <Text style={styles.compareModeCancel}>Cancel</Text>
          </TouchableOpacity>
        </View>
      )}
    </SafeAreaView>
  );
};

const getStyles = (darkMode) => StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: darkMode ? '#1a202c' : '#f0f4f8',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
  },
  backButton: {
    padding: 8,
  },
  headerTitleContainer: {
    flex: 1,
    marginHorizontal: 16,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#ffffff',
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 1, height: 1 },
    textShadowRadius: 3,
  },
  headerSubtitle: {
    fontSize: 12,
    color: 'rgba(255, 255, 255, 0.9)',
    marginTop: 4,
  },
  compareButton: {
    padding: 8,
    position: 'relative',
  },
  compareBadge: {
    position: 'absolute',
    top: -2,
    right: -2,
    backgroundColor: '#ef4444',
    width: 18,
    height: 18,
    borderRadius: 9,
    justifyContent: 'center',
    alignItems: 'center',
  },
  compareBadgeText: {
    fontSize: 10,
    color: '#ffffff',
    fontWeight: 'bold',
  },
  searchContainer: {
    paddingHorizontal: 20,
    paddingVertical: 12,
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
    borderBottomWidth: 1,
    borderBottomColor: darkMode ? '#4a5568' : '#e5e7eb',
  },
  searchInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: darkMode ? '#4a5568' : '#f3f4f6',
    borderRadius: 12,
    paddingHorizontal: 16,
    height: 48,
  },
  searchIcon: {
    marginRight: 12,
  },
  searchInput: {
    flex: 1,
    fontSize: 16,
    color: darkMode ? '#f7fafc' : '#1f2937',
    height: '100%',
  },
  statsContainer: {
    paddingHorizontal: 20,
    paddingVertical: 16,
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
  },
  statsGrid: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  statCard: {
    alignItems: 'center',
    flex: 1,
    padding: 8,
  },
  statNumber: {
    fontSize: 24,
    fontWeight: 'bold',
    color: darkMode ? '#f7fafc' : '#1f2937',
    marginBottom: 4,
  },
  statLabel: {
    fontSize: 12,
    color: darkMode ? '#a0aec0' : '#6b7280',
    fontWeight: '600',
  },
  statSubLabel: {
    fontSize: 10,
    color: darkMode ? '#718096' : '#9ca3af',
  },
  quickFiltersContainer: {
    paddingHorizontal: 20,
    paddingVertical: 12,
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
  },
  quickFilter: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: darkMode ? '#4a5568' : '#f3f4f6',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    marginRight: 8,
    borderWidth: 1,
    borderColor: darkMode ? '#4a5568' : '#e5e7eb',
  },
  quickFilterActive: {
    backgroundColor: '#667eea',
    borderColor: '#667eea',
  },
  quickFilterText: {
    fontSize: 14,
    color: darkMode ? '#cbd5e0' : '#6b7280',
    marginLeft: 6,
  },
  quickFilterTextActive: {
    color: '#ffffff',
  },
  actionButtonsContainer: {
    flexDirection: 'row',
    paddingHorizontal: 20,
    paddingVertical: 12,
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
    borderBottomWidth: 1,
    borderBottomColor: darkMode ? '#4a5568' : '#e5e7eb',
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: darkMode ? '#4a5568' : '#f3f4f6',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 20,
    marginRight: 8,
    position: 'relative',
  },
  compareActionButton: {
    backgroundColor: '#667eea',
    flex: 1,
    justifyContent: 'center',
  },
  actionButtonText: {
    fontSize: 14,
    color: darkMode ? '#cbd5e0' : '#4b5563',
    marginLeft: 6,
  },
  actionBadge: {
    position: 'absolute',
    top: 6,
    right: 6,
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#667eea',
  },
  listContent: {
    paddingHorizontal: 20,
    paddingVertical: 16,
  },
  universityCard: {
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
    borderRadius: 16,
    marginBottom: 16,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  universityCardSelected: {
    borderWidth: 2,
    borderColor: '#667eea',
  },
  universityCardExpanded: {
    shadowColor: '#667eea',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.3,
    shadowRadius: 16,
    elevation: 8,
  },
  universityCardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
  },
  universityHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  rankingBadge: {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  rankingText: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#ffffff',
  },
  universityBasicInfo: {
    flex: 1,
  },
  universityName: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#ffffff',
    marginBottom: 4,
  },
  universityLocation: {
    fontSize: 12,
    color: 'rgba(255, 255, 255, 0.9)',
  },
  universityHeaderRight: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  favoriteButton: {
    padding: 4,
    marginRight: 8,
  },
  compareCheckbox: {
    padding: 4,
  },
  universityCardContent: {
    padding: 16,
  },
  universityDescription: {
    fontSize: 14,
    color: darkMode ? '#cbd5e0' : '#4b5563',
    lineHeight: 20,
    marginBottom: 16,
  },
  universityStats: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  statItemSmall: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  statTextSmall: {
    fontSize: 12,
    color: darkMode ? '#a0aec0' : '#6b7280',
    marginLeft: 4,
  },
  specializationsContainer: {
    marginBottom: 16,
  },
  specializationsTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: darkMode ? '#f7fafc' : '#1f2937',
    marginBottom: 8,
  },
  specializationsList: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  specializationTag: {
    backgroundColor: darkMode ? '#4a5568' : '#e5e7eb',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
  },
  specializationText: {
    fontSize: 12,
    color: darkMode ? '#cbd5e0' : '#4b5563',
  },
  moreTag: {
    backgroundColor: '#667eea',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
  },
  moreText: {
    fontSize: 12,
    color: '#ffffff',
  },
  expandedDetails: {
    backgroundColor: darkMode ? '#4a5568' : '#f3f4f6',
    padding: 12,
    borderRadius: 8,
    marginBottom: 16,
  },
  detailRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 6,
  },
  detailLabel: {
    fontSize: 12,
    color: darkMode ? '#a0aec0' : '#6b7280',
    marginLeft: 6,
  },
  detailValue: {
    fontSize: 12,
    fontWeight: '600',
    color: darkMode ? '#f7fafc' : '#1f2937',
    marginLeft: 4,
  },
  universityActions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  actionButtonSmall: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    flex: 1,
    marginHorizontal: 4,
    justifyContent: 'center',
  },
  websiteButton: {
    backgroundColor: '#3b82f6',
  },
  applyButton: {
    backgroundColor: '#10b981',
  },
  shareButton: {
    backgroundColor: '#8b5cf6',
  },
  actionButtonSmallText: {
    color: '#ffffff',
    fontSize: 12,
    fontWeight: '600',
    marginLeft: 4,
  },
  mapContainer: {
    flex: 1,
  },
  map: {
    flex: 1,
  },
  mapMarker: {
    alignItems: 'center',
  },
  markerGradient: {
    width: 36,
    height: 36,
    borderRadius: 18,
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.3,
    shadowRadius: 4,
    elevation: 4,
  },
  markerText: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#ffffff',
  },
  mapLegend: {
    position: 'absolute',
    bottom: 20,
    left: 20,
    right: 20,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    padding: 12,
    borderRadius: 8,
    alignItems: 'center',
  },
  mapLegendText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  filtersModal: {
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: '80%',
  },
  sortModal: {
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: '60%',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: darkMode ? '#4a5568' : '#e5e7eb',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: darkMode ? '#f7fafc' : '#1f2937',
  },
  filtersList: {
    padding: 20,
  },
  filterItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: darkMode ? '#4a5568' : '#f3f4f6',
  },
  filterItemActive: {
    backgroundColor: darkMode ? '#4a5568' : '#f3f4f6',
  },
  filterIconContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: darkMode ? '#4a5568' : '#f3f4f6',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  filterText: {
    flex: 1,
    fontSize: 16,
    color: darkMode ? '#f7fafc' : '#1f2937',
  },
  filterTextActive: {
    color: '#667eea',
    fontWeight: '600',
  },
  clearFiltersButton: {
    padding: 20,
    alignItems: 'center',
    borderTopWidth: 1,
    borderTopColor: darkMode ? '#4a5568' : '#e5e7eb',
  },
  clearFiltersText: {
    fontSize: 16,
    color: '#667eea',
    fontWeight: '600',
  },
  sortOptionsList: {
    padding: 20,
  },
  sortItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: darkMode ? '#4a5568' : '#f3f4f6',
  },
  sortItemActive: {
    backgroundColor: darkMode ? '#4a5568' : '#f3f4f6',
  },
  sortIconContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: darkMode ? '#4a5568' : '#f3f4f6',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  sortText: {
    flex: 1,
    fontSize: 16,
    color: darkMode ? '#f7fafc' : '#1f2937',
  },
  sortTextActive: {
    color: '#667eea',
    fontWeight: '600',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: darkMode ? '#1a202c' : '#f0f4f8',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: darkMode ? '#cbd5e0' : '#6b7280',
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: darkMode ? '#1a202c' : '#f0f4f8',
    paddingHorizontal: 32,
  },
  errorTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: darkMode ? '#f7fafc' : '#1f2937',
    marginTop: 16,
  },
  errorText: {
    fontSize: 16,
    color: darkMode ? '#cbd5e0' : '#6b7280',
    textAlign: 'center',
    marginVertical: 16,
  },
  retryButton: {
    backgroundColor: '#667eea',
    paddingHorizontal: 32,
    paddingVertical: 12,
    borderRadius: 8,
  },
  retryButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '600',
  },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 48,
  },
  emptyStateTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: darkMode ? '#f7fafc' : '#1f2937',
    marginTop: 16,
  },
  emptyStateText: {
    fontSize: 16,
    color: darkMode ? '#a0aec0' : '#6b7280',
    textAlign: 'center',
    marginVertical: 16,
    maxWidth: '80%',
  },
  emptyStateButton: {
    backgroundColor: '#667eea',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
  },
  emptyStateButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '600',
  },
  footer: {
    paddingVertical: 24,
    alignItems: 'center',
  },
  footerText: {
    fontSize: 14,
    color: darkMode ? '#a0aec0' : '#6b7280',
    marginBottom: 8,
  },
  footerNote: {
    fontSize: 12,
    color: darkMode ? '#718096' : '#9ca3af',
    textAlign: 'center',
    fontStyle: 'italic',
  },
  compareModeBanner: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: '#667eea',
    padding: 16,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  compareModeText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600',
  },
  compareModeCancel: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600',
    textDecorationLine: 'underline',
  },
});

export default UKUniversities;